import { useEffect, useRef } from 'react';
import { Search, X } from 'lucide-react';
import { useDropdown } from '@/hooks/useDropdown';

interface SearchModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function SearchModal({ isOpen, onClose }: SearchModalProps) {
  const inputRef = useRef<HTMLInputElement>(null);

  // Use the dropdown hook for escape key handling
  useDropdown({
    isOpen,
    onClose,
    closeOnClickOutside: false, // We handle backdrop clicks manually
    closeOnEscape: true,
  });

  // Focus input when modal opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="search-modal-label"
      className="fixed inset-0 z-50 bg-background/80 backdrop-blur-sm"
      onClick={onClose}
    >
      <div className="container mx-auto px-4 pt-4">
        <div
          className="rounded-lg border border-border bg-background-elevated shadow-lg"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center gap-2 p-3">
            <Search className="h-5 w-5 text-foreground-subtle" aria-hidden="true" />
            <input
              ref={inputRef}
              type="text"
              placeholder="Search..."
              aria-label="Search"
              id="search-modal-label"
              className="
                flex-1 bg-transparent text-foreground placeholder:text-foreground-subtle
                focus:outline-none
              "
            />
            <button
              onClick={onClose}
              aria-label="Close search"
              className="
                rounded p-1 text-foreground-muted hover:bg-background-hover
                hover:text-foreground transition-colors
              "
            >
              <X className="h-5 w-5" aria-hidden="true" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
